import CASBlock from "@blocks/CASBlock/CASBlock";
import FestivalBanner from "@blocks/FestivalBanner/FestivalBanner";
import FestivalWrapper from "@blocks/FestivalBanner/FestivalWrapper";
import { QnFloatButton } from "@components/Layouts/QuickNews/QnButtons";
import { pubPageAdTargeting } from "@config/AdsConstants";
import { NDP_2026_TAG, NDP_2026_TAGNAME } from "@config/KeywordConstants";
import CatfishAd from "@elements/Advertisement/CatfishAd";
import { GoogleAdsSlotFactory } from "@elements/Advertisement/helpers";
import PrestitialAd from "@elements/Advertisement/PrestitialAd";
import { AdsLb1 } from "@elements/Advertisement/variant/AdsLb";
import { Footer } from "@footer/Footer";
import { Header } from "@header/Header";
import {
  HEADER_MAIN_MENU,
  HEADER_MOBILE_MAIN_MENU,
} from "@header/HeaderMenuConstants";
import { HeaderMenuList } from "@header/HeaderMenuList";
import {
  CAS_ALL_PAGE_CAMPAIGN_ID,
  FESTIVAL_FLAG,
  NDP_BANNER_FLAG,
} from "@helper/getEnvVariables";
import getPageDetails from "@helper/getPageDetails";
import { useNeuronPageViewPush } from "@hooks/useAnalyticsCall/useNeuronPageViewPush";
import useCasScript from "@hooks/useCasScript";
import { usePageScroll } from "@hooks/usePageScroll";
import { NoSSR } from "@sphtech/web2-core/components";
import { AdTargetingType, PageAdTargetValue } from "@typings/Ads.d";
import { ReactElement, ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";

import EventBanner from "../EventBanner/EventBanner";

export type AdsSettings = {
  isPrestitialEnabled: boolean | undefined;
  isCatfishEnabled: boolean | undefined;
  isTaboolaEnabled: boolean | undefined;
};

export type MainContainerProps = Readonly<{
  /**
   * Test ID used to identify the main container.
   * @default "main-container"
   */
  testId?: string;

  /**
   * Page content rendered inside the main container.
   */
  children: ReactNode;

  /**
   * Section name related to page.
   */
  sectionName?: string;

  /**
   * Checks whether the LB1 ad is displayed.
   */
  hasLb1Ads?: boolean;

  /**
   * Checks whether the page is rendered in a mobile webview.
   * @default false
   */
  mobwebview?: boolean;

  /**
   * Page-level advertisement targeting value.
   */
  pageAdTargetValue: PageAdTargetValue;

  /**
   * Ad settings supplied to the page.
   */
  suppliedAdsSettings: AdsSettings;

  /**
   * Google ad channels used for advertisement targeting.
   */
  gsChannels?: string[];

  /**
   * Checks whether the GE home widget is enabled.
   */
  geHomeWidget?: boolean;

  /**
   * Checks whether the current page is an article page.
   * @default false
   */
  isArticlePage?: boolean;

  /**
   * Checks whether the newsletter signup is displayed.
   * @default true
   */
  showNewsletterSignup?: boolean;

  /**
   * Checks whether the page uses a special background.
   * @default false
   */
  specialBackground?: boolean;

  /**
   * Checks whether the word puzzle is expanded.
   * @default false
   */
  isWordPuzzleExpanded?: boolean;

  /**
   * Checks whether the event banner is displayed.
   * @default true
   */
  eventBanner?: boolean;

  /**
   * Checks whether the article first-fold design is enabled.
   * @default false
   */
  isArticleFirstFoldEnabled?: boolean;
}>;

/**
 * Main layout container that renders the header, navigation,
 * page content, ads, and footer.
 *
 * Container to provide a fixed width and horizontal padding around the content. Typically paired
 * with a Layout component to create reusable templates across all pages. Width of child content as
 * follows with each screen size:

  Viewport width >= 1280: Children component width: 1150px
  Viewport width >= 1024: Children component width: 990px
  Viewport width >= 992: Children component width: 960px
  Viewport width >= 768: Children component width: 720px
  Viewport width >= 576: Children component width: 540px
  All other viewport widths: Children component width: 100% - padding 30px

 * @param {ReactElement} children - Any React Element but likely used to wrap a Tamil Murasu module
 * @param {String} testId - Unique string used to target elements when testing
 * @returns {ReactElement} - A wrapped module with a fixed width that resizes on view port resize
 */
export default function MainContainer({
  children,
  testId = "main-container",
  sectionName,
  hasLb1Ads = true,
  mobwebview = false,
  pageAdTargetValue,
  gsChannels,
  suppliedAdsSettings,
  showNewsletterSignup = true,
  specialBackground = false,
  isArticlePage = false,
  isWordPuzzleExpanded = false,
  eventBanner = true,
  isArticleFirstFoldEnabled = false,
}: MainContainerProps): ReactElement {
  const { hasDesktopNav } = getPageDetails(sectionName) || {
    hasDesktopNav: true,
  };

  useNeuronPageViewPush();
  const { isScriptReady: isCasScriptReady } = useCasScript();

  sectionName = sectionName == "/" ? "home" : sectionName;
  const pageAdTarget = pubPageAdTargeting[pageAdTargetValue] as AdTargetingType<
    string,
    PageAdTargetValue
  >;

  // sets the initial starting point to 256px, when user scroll the header will stay for a while before it hides.
  const scrollDir = usePageScroll({ initialStart: 256 });

  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // Sets quick news reference code.
  const qnRefcode =
    sectionName === "home"
      ? "home-quick-news"
      : !isArticlePage
        ? "listing-quick-news"
        : "article-quick-news";

  return (
    <>
      <FestivalWrapper
        className={twMerge(
          "festival",
          (sectionName === "general-election-2025" || specialBackground) &&
            "bg-gray-500",
        )}
      >
        {hasLb1Ads && !mobwebview && isArticleFirstFoldEnabled && (
          <AdsLb1
            sectionName={sectionName}
            pageAdTargetType={pageAdTargetValue}
            gsChannels={gsChannels}
            containerClass="pt-5 !pb-5 bg-gray-500/50"
          />
        )}

        {FESTIVAL_FLAG && <FestivalBanner />}

        {!mobwebview && (
          <Header
            sectionName={sectionName}
            scrollDir={scrollDir}
            isSearchExpanded={isSearchExpanded}
            setIsSearchExpanded={setIsSearchExpanded}
            isArticleFirstFoldEnabled={isArticleFirstFoldEnabled}
          />
        )}

        {hasDesktopNav && (
          <div className="hidden lg:block sticky top-0 z-50 bg-white-100 w-full">
            <HeaderMenuList
              testId="desktop-main-links"
              items={HEADER_MAIN_MENU}
              type="main"
              isHamburger={false}
              isArticleFirstFoldEnabled={isArticleFirstFoldEnabled}
            />
          </div>
        )}

        {!isArticlePage && !mobwebview && (
          <div
            className={twMerge(
              "mobile-menu lg:hidden block z-40 bg-white-100 w-full sticky transition-all duration-1000 ease-in-out",
              scrollDir === "down"
                ? "top-0 translate-y-0"
                : isSearchExpanded
                  ? "top-32 translate-y-1"
                  : "top-[52px] translate-y-1",
            )}
          >
            <HeaderMenuList
              testId="mobile-main-links"
              items={HEADER_MOBILE_MAIN_MENU}
              type="mobile-main"
              isHamburger={false}
              sectionName={sectionName}
            />
          </div>
        )}

        {isCasScriptReady && CAS_ALL_PAGE_CAMPAIGN_ID && (
          <CASBlock casId={CAS_ALL_PAGE_CAMPAIGN_ID} />
        )}

        {NDP_BANNER_FLAG == "1" && eventBanner && (
          <EventBanner
            link={`/keywords/${NDP_2026_TAG}?ref=ndp-banner-web`}
            title={NDP_2026_TAGNAME}
          />
        )}

        {hasLb1Ads && !mobwebview && !isArticleFirstFoldEnabled && (
          <AdsLb1
            sectionName={sectionName}
            pageAdTargetType={pageAdTargetValue}
            gsChannels={gsChannels}
          />
        )}

        {(sectionName === "general-election-2025" || specialBackground) && (
          <div className="w-full h-2.5 bg-gradient-to-r from-pink-701 to-pink-301 mb-5 relative" />
        )}

        <NoSSR>
          <QnFloatButton
            refCode={qnRefcode}
            isCatfishEnabled={suppliedAdsSettings.isCatfishEnabled}
            isWordPuzzleExpanded={isWordPuzzleExpanded}
          />
        </NoSSR>

        <div
          data-testid={testId}
          id={testId}
          className={twMerge(
            "xl:max-w-xl w-full mx-auto xxs:px-[15px] lg:p-0 max-xsm:px-1",
            sectionName === "about-us" &&
              "xl:max-w-full xxs:!px-0 max-xsm:!px-0",
            sectionName === "videos" && "xxs:px-0",
            isArticleFirstFoldEnabled && "pt-5 lg:pt-2.5",
          )}
        >
          <PrestitialAd
            slot={GoogleAdsSlotFactory.prestitial(sectionName)}
            isPrestitialEnabled={suppliedAdsSettings.isPrestitialEnabled}
            pubAdTargeting={[pageAdTarget]}
            gsChannels={gsChannels}
          />
          {children}
        </div>
        {!mobwebview && (
          <Footer
            className={sectionName === "about-us" ? "mt-0 lg:mt-500" : ""}
            showNewsletterSignup={showNewsletterSignup}
          />
        )}
        <CatfishAd
          slot={GoogleAdsSlotFactory.catfish(sectionName)}
          isCatfishEnabled={suppliedAdsSettings.isCatfishEnabled}
          pubAdTargeting={[pageAdTarget]}
          gsChannels={gsChannels}
        />
      </FestivalWrapper>
    </>
  );
}
